import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import {
  registerUser,
  loginUser,
  logoutUser,
  onAuthStateChanged,
  loginWithGoogle,
  loginWithApple,
  resetPassword
} from '../services/authService';
import { UserProfile, userService } from '../services/userService';
import { SubscriptionStatus, MembershipType } from '../types/saas';
import { getTeamMembers, ensureTeamSetup } from '../services/team.service';
import { getSubscription, createTrialSubscription, isSubscriptionValid } from '../services/subscriptionService';
import { isAdmin as isAdminCheck } from '../config/adminConfig';
import { toast } from 'react-hot-toast';
import logger from '../utils/logger';
import { Session } from '@supabase/supabase-js';

const EMAIL_DOMAIN = process.env.REACT_APP_EMAIL_DOMAIN || 'atlasdefteri.com';

interface AuthContextType {
  user: UserProfile | null;
  userProfile: UserProfile | null;
  needsPhone: boolean;
  completePhoneSetup: (phone: string) => Promise<void>;
  team: any | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  subscriptionStatus: SubscriptionStatus;
  teamSubscriptionStatus: SubscriptionStatus;
  daysRemaining: number | null;
  subscriptionEndDate: Date | null;
  teamMembers: UserProfile[];
  currentUserRole: string;
  canWrite: boolean;
  canRead: boolean;
  login: (phoneNumber?: string, membershipType?: MembershipType, desktopCode?: string, password?: string) => Promise<void>;
  loginWithProfile: (userProfile: UserProfile) => Promise<void>;
  loginGoogle: () => Promise<void>;
  loginApple: () => Promise<void>;
  resetUserPassword: (email: string) => Promise<void>;
  register: (phoneNumber?: string, name?: string, email?: string, teamCode?: string, membershipType?: MembershipType, desktopCode?: string, password?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  checkSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [team, setTeam] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>(SubscriptionStatus.ACTIVE);
  const [teamSubscriptionStatus, setTeamSubscriptionStatus] = useState<SubscriptionStatus>(SubscriptionStatus.ACTIVE);
  const [teamMembers, setTeamMembers] = useState<UserProfile[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<string>('');
  const [canWrite, setCanWrite] = useState<boolean>(true);
  const [canRead] = useState<boolean>(true);
  const [needsPhone, setNeedsPhone] = useState<boolean>(false);
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<Date | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session: initSession }, error } = await supabase.auth.getSession();
        if (error) throw error;

        setSession(initSession);

        if (initSession?.user) {
          await loadUserProfile(initSession.user.id);
        }
      } catch (error) {
        logger.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    const unsubscribe = onAuthStateChanged(async (newSession: Session | null) => {
      setSession(newSession);
      if (newSession?.user) {
        setIsLoading(true);
        try {
          await loadUserProfile(newSession.user.id);
        } catch (e) {
          logger.error('Auth state change profile load error:', e);
        } finally {
          setIsLoading(false);
        }
      } else {
        setUserProfile(null);
        setTeam(null);
        setTeamMembers([]);
        setCurrentUserRole('');
        setCanWrite(false);
        setIsLoading(false);
      }
    });

    return () => {
      if (unsubscribe && typeof unsubscribe.unsubscribe === 'function') {
        unsubscribe.unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserProfile = async (userId: string) => {
    try {
      let profile = await userService.getUserProfile(userId);

      // İlk girişte eğer user_profiles'da kayıt yoksa → oluştur
      if (!profile) {
        // Supabase Auth'dan kullanıcı bilgilerini al (Google/Apple metadata)
        const { data: { user: authUser } } = await supabase.auth.getUser();
        const meta = authUser?.user_metadata || {};
        const fullName = meta.full_name || meta.name || '';
        const firstName = fullName.split(' ')[0] || meta.given_name || 'Yeni';
        const lastName = fullName.split(' ').slice(1).join(' ') || meta.family_name || 'Kullanıcı';
        const userEmail = authUser?.email || meta.email || '';
        const phoneFromEmail = userEmail.replace('@' + EMAIL_DOMAIN, '');
        const phoneNumber = /^\d+$/.test(phoneFromEmail) ? phoneFromEmail : '';

        // 1. Önce mevcut takımı kontrol et (duplicate önleme)
        const { data: existingTeam } = await supabase
          .from('teams')
          .select('*')
          .eq('owner_id', userId)
          .maybeSingle();

        let teamId: string;

        if (existingTeam) {
          teamId = existingTeam.id;
        } else {
          // Yeni takım oluştur
          const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
          const inviteCode = Array.from({ length: 6 }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');

          const { data: newTeam, error: teamError } = await supabase
            .from('teams')
            .insert({
              name: `${firstName} Takım`,
              status: 'active',
              invite_code: inviteCode,
              owner_id: userId
            })
            .select()
            .single();

          if (teamError) {
            logger.error('Takım oluşturma hatası:', teamError);
            throw teamError;
          }
          teamId = newTeam.id;
        }

        // 2. user_profiles upsert (duplicate key hatası önleme)
        const { error: profileError } = await supabase
          .from('user_profiles')
          .upsert({
            id: userId,
            first_name: firstName,
            last_name: lastName,
            phone_number: phoneNumber || null,
            email: userEmail || null,
            role: 'super_admin',
            team_id: teamId,
            team_role: 'OWNER',
            is_active: true
          }, { onConflict: 'id' });

        if (profileError) {
          logger.error('Profil upsert hatası:', profileError);
          throw profileError;
        }

        // Trial abonelik oluştur
        await createTrialSubscription(userId, teamId);

        // Profili tekrar çek
        profile = await userService.getUserProfile(userId);
      }

      // Google/Apple ile giriş yapılmış ve telefon numarası yoksa sor
      if (profile && !profile.phone_number) {
        setNeedsPhone(true);
      } else {
        setNeedsPhone(false);
      }

      setUserProfile(profile);
      console.log('[AuthContext] User profile loaded:', profile?.id, profile?.role);

      if (profile?.team_id) {
        // Eksik invite_code / owner_id varsa düzelt
        await ensureTeamSetup(profile.team_id, userId);

        const { data: teamData } = await supabase.from('teams').select('*').limit(100).eq('id', profile.team_id).single();
        setTeam(teamData);

        // Owner kontrolü: team_role veya owner_id eşleşmesi
        const isTeamOwner = teamData?.owner_id === userId;
        let teamRole = (profile?.team_role || '').toUpperCase();

        // owner_id eşleşiyorsa ama team_role OWNER değilse düzelt
        if (isTeamOwner && teamRole !== 'OWNER') {
          teamRole = 'OWNER';
          // DB'de de düzelt
          await supabase.from('user_profiles').update({ team_role: 'OWNER' }).eq('id', userId);
        }

        setCurrentUserRole(teamRole);
        const writeAllowed = profile?.role === 'super_admin' || ['OWNER', 'ADMIN', 'MEMBER'].includes(teamRole);
        setCanWrite(writeAllowed);

        // Ekip üyelerini yükle
        try {
          const members = await getTeamMembers(profile.team_id);
          setTeamMembers(members);
        } catch (e) {
          logger.error('Team members load error:', e);
          setTeamMembers([]);
        }
      } else {
        setTeamMembers([]);
        setCurrentUserRole('');
        setCanWrite(profile?.role === 'super_admin');
      }

      await checkSubscription(userId);
    } catch (error) {
      logger.error('Profile load error:', error);
      // Hata durumunda state'i temizle ve loading'i kapat
      setUserProfile(null);
      setTeam(null);
      setTeamMembers([]);
      setCurrentUserRole('');
      setCanWrite(false);
      setIsLoading(false);
      toast.error('Profil yüklenemedi. Lütfen tekrar giriş yapın.');
      // Oturumu kapat ve login sayfasına yönlendir
      try {
        await supabase.auth.signOut();
      } catch (_) {}
      setSession(null);
      window.location.href = '/';
    }
  };

  const checkSubscription = async (userId?: string) => {
    if (!userId) {
      setSubscriptionStatus(SubscriptionStatus.ACTIVE);
      setTeamSubscriptionStatus(SubscriptionStatus.ACTIVE);
      setDaysRemaining(null);
      setSubscriptionEndDate(null);
      return;
    }

    try {
      // Super admin → always ACTIVE
      const currentProfile = userProfile;
      if (isAdminCheck(currentProfile)) {
        setSubscriptionStatus(SubscriptionStatus.ACTIVE);
        setTeamSubscriptionStatus(SubscriptionStatus.ACTIVE);
        setDaysRemaining(null);
        setSubscriptionEndDate(null);
        return;
      }

      let sub = await getSubscription(userId);

      // No subscription → create trial
      if (!sub) {
        sub = await createTrialSubscription(userId, currentProfile?.team_id || undefined);
        if (sub) {
          const endDate = new Date(sub.end_date);
          const days = Math.max(0, Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
          setDaysRemaining(days);
          setSubscriptionEndDate(endDate);
          setSubscriptionStatus(SubscriptionStatus.TRIAL);
          setTeamSubscriptionStatus(SubscriptionStatus.TRIAL);
          return;
        }
        setSubscriptionStatus(SubscriptionStatus.TRIAL);
        setTeamSubscriptionStatus(SubscriptionStatus.TRIAL);
        setDaysRemaining(7);
        setSubscriptionEndDate(null);
        return;
      }

      // Kalan gün hesapla
      const endDate = new Date(sub.end_date);
      const days = Math.max(0, Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
      setDaysRemaining(days);
      setSubscriptionEndDate(endDate);

      // Otomatik expire kontrolü: end_date geçmişse status'u expired yap
      if (endDate < new Date() && sub.status !== 'expired' && sub.status !== 'suspended') {
        await supabase
          .from('subscriptions')
          .update({ status: 'expired' })
          .eq('id', sub.id);
        setSubscriptionStatus(SubscriptionStatus.EXPIRED);
        setTeamSubscriptionStatus(SubscriptionStatus.EXPIRED);
        return;
      }

      const validity = isSubscriptionValid(sub, currentProfile?.role);

      if (sub.status === 'suspended') {
        setSubscriptionStatus(SubscriptionStatus.SUSPENDED);
        setTeamSubscriptionStatus(SubscriptionStatus.SUSPENDED);
      } else if (validity.valid) {
        const status = sub.status === 'trial' ? SubscriptionStatus.TRIAL : SubscriptionStatus.ACTIVE;
        setSubscriptionStatus(status);
        setTeamSubscriptionStatus(status);
      } else {
        setSubscriptionStatus(SubscriptionStatus.EXPIRED);
        setTeamSubscriptionStatus(SubscriptionStatus.EXPIRED);
      }
    } catch (error) {
      logger.error('Subscription check error:', error);
      setSubscriptionStatus(SubscriptionStatus.ACTIVE);
      setTeamSubscriptionStatus(SubscriptionStatus.ACTIVE);
      setDaysRemaining(null);
      setSubscriptionEndDate(null);
    }
  };

  const login = async (phoneNumber?: string, membershipType?: MembershipType, desktopCode?: string, password?: string) => {
    try {
      setIsLoading(true);
      const response = await loginUser({ phoneNumber, membershipType, desktopCode, password });

      setSession(response.session);
      if (response.user) {
        await loadUserProfile(response.user.id);
      }

      toast.success('Giriş başarılı!');
    } catch (error: any) {
      logger.error('Login error:', error);
      toast.error(error.message || 'Giriş yapılamadı');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithProfile = async (profile: UserProfile) => {
    try {
      setIsLoading(true);
      setUserProfile(profile);
      await checkSubscription(profile.id);
      toast.success(`Hoş geldiniz ${profile.first_name}!`);
    } catch (error: any) {
      logger.error('Login with profile error:', error);
      toast.error(error.message || 'Giriş yapılamadı');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginGoogle = async () => {
    try {
      await loginWithGoogle();
    } catch (error: any) {
      logger.error('Google login error:', error);
      toast.error(error.message || 'Google ile giriş yapılamadı');
      throw error;
    }
  };

  const loginApple = async () => {
    try {
      await loginWithApple();
    } catch (error: any) {
      logger.error('Apple login error:', error);
      toast.error(error.message || 'Apple ile giriş yapılamadı');
      throw error;
    }
  };

  const resetUserPassword = async (email: string) => {
    try {
      await resetPassword(email);
      toast.success('Şifre sıfırlama e-postası gönderildi!');
    } catch (error: any) {
      logger.error('Reset password error:', error);
      toast.error(error.message || 'Şifre sıfırlama başarısız');
      throw error;
    }
  };

  const register = async (phoneNumber?: string, name?: string, email?: string, teamCode?: string, membershipType?: MembershipType, desktopCode?: string, password?: string) => {
    try {
      setIsLoading(true);
      const response = await registerUser({
        phoneNumber,
        name,
        email,
        teamCode,
        membershipType,
        desktopCode,
        password
      });

      setSession(response.session);
      if (response.user) {
        await loadUserProfile(response.user.id);
      }

      toast.success('Kayıt başarılı!');
    } catch (error: any) {
      logger.error('Register error:', error);
      toast.error(error.message || 'Kayıt yapılamadı');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await logoutUser();
    } catch (error) {
      logger.error('Logout error:', error);
      // signOut başarısız olsa bile oturumu temizle
      try { await supabase.auth.signOut(); } catch (_) {}
    } finally {
      setSession(null);
      setUserProfile(null);
      setTeam(null);
      setTeamMembers([]);
      setCurrentUserRole('');
      setCanWrite(false);
      setIsLoading(false);
      // Sayfayı yenile — kesin çıkış
      window.location.href = '/';
    }
  };

  const refreshUser = async () => {
    if (session?.user?.id) {
      await loadUserProfile(session.user.id);
    }
  };

  // Google/Apple ile giriş yapan kullanıcı telefon numarasını tamamlıyor
  const completePhoneSetup = async (phone: string) => {
    if (!session?.user?.id || !phone) return;
    const digits = phone.replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '');
    const normalized = '0' + digits;
    const { error } = await supabase
      .from('user_profiles')
      .update({ phone_number: normalized })
      .eq('id', session.user.id);
    if (error) throw error;

    // Eğer mevcut email kaydı varsa (telefon ile kayıtlı), o kayıtla birleştir
    const existingEmail = digits + '@' + EMAIL_DOMAIN;
    const { data: existing } = await supabase
      .from('user_profiles')
      .select('id, email')
      .eq('phone_number', normalized)
      .neq('id', session.user.id)
      .maybeSingle();

    if (existing) {
      // Çakışan eski kaydın email'ini yeni profile kopyala
      await supabase.from('user_profiles').update({ email: existing.email || existingEmail }).eq('id', session.user.id);
      // Eski kaydı sil (telefon başka auth kaydına ait)
      await supabase.from('user_profiles').delete().eq('id', existing.id);
    }

    setNeedsPhone(false);
    await loadUserProfile(session.user.id);
    toast.success('Telefon numarası kaydedildi!');
  };

  const value: AuthContextType = {
    user: userProfile,
    userProfile: userProfile,
    needsPhone,
    completePhoneSetup,
    team,
    session,
    isLoading,
    isAuthenticated: !!session,
    subscriptionStatus,
    teamSubscriptionStatus,
    daysRemaining,
    subscriptionEndDate,
    teamMembers,
    currentUserRole,
    canWrite,
    canRead,
    login,
    loginWithProfile,
    loginGoogle,
    loginApple,
    resetUserPassword,
    register,
    logout,
    refreshUser,
    checkSubscription: () => checkSubscription()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
